import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Play, Pause, Mail, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useApiMutation } from "@/hooks/useApiMutation";


export default function EmailSequences() {
  
  const queryClient = useQueryClient();
  const [showEditor, setShowEditor] = useState(false);
  const [showUnsubscribes, setShowUnsubscribes] = useState(false);
  const [editingSequence, setEditingSequence] = useState<any>(null);
  const [unsubscribeEmail, setUnsubscribeEmail] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    trigger: "signup", // signup, subscription, cancellation
    active: true,
    emails: [
      { subject: "", htmlContent: "", delayDays: 0 }
    ],
  });

  const { data: sequences = [] } = useQuery({
    queryKey: ["/api/admin/email-sequences"],
    queryFn: () => apiRequest("GET", "/api/admin/email-sequences") as unknown as Promise<any[]>,
  });

  const { data: unsubscribed = [] } = useQuery({
    queryKey: ["/api/admin/email-sequences/unsubscribed"],
    queryFn: () => apiRequest("GET", "/api/admin/email-sequences/unsubscribed") as unknown as Promise<any[]>,
  });

  const saveMutation = useApiMutation({
    endpoint: (vars: any) => editingSequence ? `/api/admin/email-sequences/${editingSequence._id}` : "/api/admin/email-sequences",
    method: (vars: any) => editingSequence ? "PATCH" : "POST",
    invalidate: [["/api/admin/email-sequences"]],
    successMessage: (data: any, vars: any) => editingSequence ? "Sequence updated" : "Sequence created",
    onSuccess: () => {
      setShowEditor(false);
      resetForm();
    },
  });

  const toggleMutation = useApiMutation({
    endpoint: (vars: { id: string; active?: boolean }) => `/api/admin/email-sequences/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/admin/email-sequences"]],
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/email-sequences/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/admin/email-sequences"]],
    successMessage: "Sequence deleted",
  });

  const unsubscribeMutation = useApiMutation({
    endpoint: "/api/admin/email-sequences/unsubscribe",
    method: "POST",
    invalidate: [["/api/admin/email-sequences/unsubscribed"]],
    successMessage: "User unsubscribed",
    onSuccess: () => {
      setUnsubscribeEmail("");
    },
  });

  const resubscribeMutation = useApiMutation({
    endpoint: "/api/admin/email-sequences/resubscribe",
    method: "POST",
    invalidate: [["/api/admin/email-sequences/unsubscribed"]],
    successMessage: "User resubscribed",
  });

  const resetForm = () => {
    setFormData({
      name: "",
      trigger: "signup",
      active: true,
      emails: [{ subject: "", htmlContent: "", delayDays: 0 }],
    });
    setEditingSequence(null);
  };

  const addEmail = () => {
    setFormData({
      ...formData,
      emails: [...formData.emails, { subject: "", htmlContent: "", delayDays: 1 }],
    });
  };

  const removeEmail = (index: number) => {
    setFormData({
      ...formData,
      emails: formData.emails.filter((_, i) => i !== index),
    });
  };

  const updateEmail = (index: number, field: string, value: any) => {
    const updated = [...formData.emails];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, emails: updated });
  };

  const handleEdit = (sequence: any) => {
    setEditingSequence(sequence);
    setFormData({
      name: sequence.name,
      trigger: sequence.trigger,
      active: sequence.active,
      emails: sequence.emails,
    });
    setShowEditor(true);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h1 className="text-xl sm:text-3xl font-bold">Email Sequences</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUnsubscribes(true)}>
            <Users className="h-4 w-4 mr-2" />
            Unsubscribed ({unsubscribed.length})
          </Button>
          <Button onClick={() => { resetForm(); setShowEditor(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Sequence
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {sequences.map((seq: any) => (
          <Card key={seq._id}>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div>
                  <CardTitle>{seq.name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Trigger: {seq.trigger} • {seq.emails.length} emails • {seq.active ? "Active" : "Paused"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleMutation.mutate({ id: seq._id, active: !seq.active })}
                  >
                    {seq.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(seq)}>
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate({ id: seq._id })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {seq.emails.map((email: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>Day {email.delayDays}:</span>
                    <span className="font-medium">{email.subject}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSequence ? "Edit" : "Create"} Email Sequence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sequence Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Welcome Series"
              />
            </div>

            <div>
              <Label>Trigger</Label>
              <Select value={formData.trigger} onValueChange={(v) => setFormData({ ...formData, trigger: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="signup">User Signup</SelectItem>
                  <SelectItem value="subscription">New Subscription</SelectItem>
                  <SelectItem value="cancellation">Subscription Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <Label>Emails in Sequence</Label>
                <Button variant="outline" size="sm" onClick={addEmail}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Email
                </Button>
              </div>

              {formData.emails.map((email, index) => (
                <Card key={index}>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex flex-wrap justify-between items-center gap-2">
                      <Label>Email #{index + 1}</Label>
                      {formData.emails.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeEmail(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div>
                      <Label>Send after (days)</Label>
                      <Input
                        type="number"
                        value={email.delayDays}
                        onChange={(e) => updateEmail(index, "delayDays", parseInt(e.target.value))}
                        min="0"
                      />
                    </div>

                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={email.subject}
                        onChange={(e) => updateEmail(index, "subject", e.target.value)}
                        placeholder="Email subject"
                      />
                    </div>

                    <div>
                      <Label>Content</Label>
                      <div className="border rounded-md">
                        <ReactQuill
                          theme="snow"
                          value={email.htmlContent}
                          onChange={(content) => updateEmail(index, "htmlContent", content)}
                          style={{ minHeight: "200px" }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button onClick={() => saveMutation.mutate(formData)} disabled={saveMutation.isPending} className="w-full">
              {saveMutation.isPending ? "Saving..." : editingSequence ? "Update Sequence" : "Create Sequence"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUnsubscribes} onOpenChange={setShowUnsubscribes}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Unsubscribed Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="email@example.com"
                value={unsubscribeEmail}
                onChange={(e) => setUnsubscribeEmail(e.target.value)}
              />
              <Button onClick={() => unsubscribeMutation.mutate({ email: unsubscribeEmail })} disabled={!unsubscribeEmail}>
                Unsubscribe
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-right p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {unsubscribed.map((item: any) => (
                    <tr key={item.email} className="border-t">
                      <td className="p-3">{item.email}</td>
                      <td className="p-3">{new Date(item.unsubscribedAt).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resubscribeMutation.mutate({ email: item.email })}
                        >
                          Resubscribe
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
